import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Card,
  CardContent,
  Grid2,
  Typography,
  Autocomplete,
  TextField,
  Box,
  Divider,
  Button as MatButton,
  Link,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import PageWrapper from "../../../shared/PageWrapper/PageWrapper";
import Button from "../../../components/Button/Button";
import PageHeader from "../../../shared/PageHeader/PageHeader";
import Search from "../../../components/Search/Search";
import { STATUS_CUSTOM_MASTER } from "../../../utils/static-masters";
import API from "../../../api/axios";
import DataNotFound from "../../../shared/DataNotFound/DataNotFound";
import DataLoader from "../../../shared/DataLoader/DataLoader";
import { INFRACON_URLS } from "../../../utils/types";
import moment from "moment";

interface Candidate {
  candidate_name?: string;
  candidate_email?: string;
  candidate_mobile?: string;
  application_date?: string;
  position_name?: string;
  application_display_status?: string;
  dob?: string;
  candidate_id?: {
    infracon_loginid?: string;
    dob?: string;
  };
}

interface ApiResponse {
  data: Candidate[];
  total_count: number;
}

const CandidatePool = () => {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<any>();
  const [candidateData, setCandidateData] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState<Boolean>(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const observer = useRef<IntersectionObserver | null>(null);
  const lastCandidateElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          setOffset((prevOffset) => prevOffset + 8);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, isFetching]
  );

  const handleStatusFilterChange = (event: any, newValue: any) => {
    setSelectedStatus(newValue);
  };

  const formatDOB = (dob: string): string => {
    if (!dob) {
      return "NA";
    }
    return `${moment(dob).format("DD-MMM-YYYY")}`;
  };

  const getBidderCandidatePool = async () => {
    if (isFetching) return;
    setIsFetching(true);
    setLoading(true);
    try {
      const response = await API.post(`/bidder/candidate-pool`, {
        search: searchValue || "",
        offset: offset,
        limit: 8,
      });
      const newData = response?.data?.data || [];

      if (newData.length === 0) {
        setHasMore(false);
      } else {
        if (offset === 0) {
          setCandidateData(newData);
        } else {
          setCandidateData((prevData) => [...prevData, ...newData]);
        }
      }
    } catch (error) {
      // Failed to find candidates
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => {
    setCandidateData([]);
    setHasMore(true);
    if (offset !== 0) {
      setOffset(0);
    } else {
      getBidderCandidatePool();
    }
  }, [searchValue]);

  useEffect(() => {
    getBidderCandidatePool();
  }, [offset]);


  return (
    <PageWrapper>
      <PageHeader title="Candidate Pool" />
      <Grid2
        container
        sx={{
          marginTop: "1rem",
        }}
      >
        <Grid2 size={{ xs: 12, sm: 3, md: 3 }} className="search-wrap">
          <Search
            label="Search"
            onSearch={(val) => {
              setSearchValue(val);
            }}
          />
        </Grid2>
        <Grid2
          size={{ xs: 12, sm: 9, md: 9 }}
          sx={{ marginTop: { xs: "10px", sm: "0px" } }}
        >
          <Box
            display="flex"
            gap={2}
            sx={{ justifyContent: { xs: "flex-start", sm: "end" } }}
          >
            {/* status filter */}
            {/* <Autocomplete
              size="small"
              disablePortal
              options={STATUS_CUSTOM_MASTER || []}
              getOptionLabel={(option) => option?.title || ""}
              value={selectedStatus} // Set selected status
              onChange={handleStatusFilterChange} // Update state on change
              componentsProps={{
                clearIndicator: { disableRipple: true },
                popupIndicator: { disableRipple: true },
              }}
              sx={{
                width: { xs: "50%", sm: "150px", md: "150px" },
                "& .MuiAutocomplete-inputRoot": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                },
                "&:hover:": {
                  border: "transparent",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  variant="outlined"
                  placeholder="Status"
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: {
                      backgroundColor: "white",
                      padding: "6px 12px",
                      "&:hover": {
                        borderColor: "#fff",
                      },
                    },
                  }}
                />
              )}
            /> */}
          </Box>
        </Grid2>
      </Grid2>
      <Card className="custom__card" sx={{ marginTop: "1.5rem" }}>
        <CardContent sx={{ padding: { xs: 0, md: "16px" } }}>
          <Grid2 container spacing={2} alignItems="stretch">
            {!!loading && candidateData.length === 0 ? (
              <Box
                sx={{
                  height: `calc(70vh - 140px)`,
                  width: `calc(80vw - 140px)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DataLoader />{" "}
              </Box>
            ) : candidateData.length > 0 ? (
              candidateData.map((candidate: any, index: any) => (
                <Grid2
                  key={index}
                  size={{ xs: 12, sm: 6, md: 6 }}
                  sx={{
                    marginBottom: { xs: "1rem", md: "2rem" },
                    display: "flex",
                  }}
                  ref={
                    index === candidateData.length - 1
                      ? lastCandidateElementRef
                      : null
                  }
                >
                  <Card
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "12px",
                      flexGrow: 1,
                      boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                    }}
                    elevation={0}
                  >
                    <CardContent
                      sx={{
                        padding: 0,
                        ":last-child": {
                          paddingBottom: "20px",
                        },
                      }}
                    >
                      <Grid2 container>
                        {/* Candidate Name Section */}
                        <Grid2
                          size={{ xs: 12 }}
                          sx={{
                            borderBottom: "1px solid #ccc",
                            padding: "1rem",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              gutterBottom
                              sx={{
                                fontWeight: 600,
                                marginBottom: "0",
                                color: "#000",
                              }}
                            >
                              {candidate?.candidate_name}
                            </Typography>
                            <Box sx={{ display: "flex", gap: "10px" }}>
                              <Typography
                                gutterBottom
                                sx={{
                                  fontWeight: 600,
                                  marginBottom: "0",
                                  color: "gray",
                                }}
                              >
                                DOB :
                              </Typography>
                              <Typography
                                gutterBottom
                                sx={{
                                  fontWeight: 500,
                                  marginBottom: "0",
                                  color: "#000",
                                }}
                              >
                                {formatDOB(candidate?.candidate_id?.dob)}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid2>

                        {/* Left Section with Empanelled Info */}
                        <Grid2
                          size={{ xs: 5 }}
                          sx={{
                            marginTop: { xs: "0.15rem", md: "0.15rem" },
                            padding: "1rem",
                          }}
                        >
                          <Box display={"flex"} flexDirection={"column"}>
                            <Typography>Empanelled for</Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                marginBottom: "0",
                                color: "#000",
                              }}
                            >
                              {candidate?.position_name}
                            </Typography>
                          </Box>
                        </Grid2>

                        {/* Vertical Divider */}
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            backgroundColor: "#ccc",
                            marginTop: "1.5rem",
                            height: "3rem !important",
                          }}
                        />

                        {/* Right Section with Infracon ID */}
                        <Grid2
                          size={{ xs: 6.9 }}
                          sx={{
                            marginTop: { xs: "0.15rem", md: "0.15rem" },
                            padding: "1rem",
                          }}
                        >
                          <Box
                            display={"flex"}
                            flexDirection={"column"}
                            alignItems={"flex-start"}
                            sx={{
                              marginLeft: {
                                md: "4rem",
                              },
                            }}
                          >
                            <Typography>Infracon ID</Typography>
                            <Box
                              display={"flex"}
                              flexDirection={"row"}
                              flexWrap={"wrap"}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  marginBottom: "0",
                                  color: "#000",
                                  paddingRight: "1rem",
                                  fontSize: "15px",
                                  wordBreak: "break-word",
                                }}
                              >
                                {candidate?.candidate_email}
                              </Typography>
                              <Link
                                href={`${INFRACON_URLS?.view_profile}${candidate?.candidate_id?.infracon_loginid}`}
                                sx={{
                                  marginBottom: "0",
                                  color: "#034ea2",
                                  cursor: "pointer",
                                  marginTop: "1em",
                                  fontFamily: "poppins",
                                }}
                                underline="none"
                                target="_blank"
                              >
                                View Profile
                              </Link>
                            </Box>
                          </Box>
                        </Grid2>
                      </Grid2>
                    </CardContent>
                  </Card>
                </Grid2>
              ))
            ) : (
              <Box
                sx={{
                  height: `calc(70vh - 140px)`,
                  width: `calc(80vw - 140px)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DataNotFound />
              </Box>
            )}
            {loading && candidateData.length > 0 && (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <DataLoader />
              </Box>
            )}
          </Grid2>
        </CardContent>
      </Card>
    </PageWrapper>
  );
};

export default CandidatePool;
